// ── Bill of Materials (BOM) ────────────────────────────────────────────
// Pipe length and fittings detection (the planner's auto-components).
// Extracted from logic.js for modularity (issue #139) and typed (issue #140).

// Catalogs loaded from API via catalog-loader.js
import { getFittings, getValves } from '../catalog/catalog-loader.ts';

// ── Types ────────────────────────────────────────────────────────────

export interface PipePoint {
  x: number;
  y: number;
}

export interface Pipe {
  id: number;
  diameter?: number;
  material?: string;
  zoneId?: number | null;
  points?: PipePoint[];
  startSnap?: { type: string; id?: number } | null;
  endSnap?: { type: string; id?: number } | null;
}

export interface ValveObject {
  id: number;
  model: string;
}

export interface Zone {
  id: number;
  name?: string;
  valveModel?: string | null;
  points?: PipePoint[];
}

export interface DripLine {
  id: number;
  model?: string;
  points?: PipePoint[];
}

export interface FittingResult {
  fitting: string;
  catalog: Record<string, unknown> | undefined;
  reason: string;
}

export interface AutoComponent extends FittingResult {
  pipeId: number;
  end?: string;
  pointIndex?: number;
}

// ── Pipe Length ─────────────────────────────────────────────────────

/** Sum point-to-point distances along a pipe's points array (in meters). */
export function calculatePipeLength(pipe: Pick<Pipe, 'points'>): number {
  if (!pipe.points || pipe.points.length < 2) return 0;
  let length = 0;
  for (let i = 1; i < pipe.points.length; i++) {
    const dx = pipe.points[i].x - pipe.points[i - 1].x;
    const dy = pipe.points[i].y - pipe.points[i - 1].y;
    length += Math.sqrt(dx * dx + dy * dy);
  }
  return Math.round(length * 1000) / 1000;
}

// ── Auto-Component Detection ────────────────────────────────────────

/** Calculate the angle (in degrees) of a direction change at a pipe vertex. */
function segmentAngle(a: PipePoint, b: PipePoint, c: PipePoint): number {
  const v1x = b.x - a.x, v1y = b.y - a.y;
  const v2x = c.x - b.x, v2y = c.y - b.y;
  const dot = v1x * v2x + v1y * v2y;
  const cross = v1x * v2y - v1y * v2x;
  const angle = Math.abs(Math.atan2(cross, dot) * 180 / Math.PI);
  return angle;
}

/** Given a pipe connection, return array of required fittings. */
export function getRequiredFittings({
  connectionType,
  pipeDiameter,
  targetDiameter,
  angle,
  valveSize,
}: {
  connectionType: string | null;
  pipeDiameter: number;
  targetDiameter?: number;
  angle?: number | null;
  valveSize?: number;
}): FittingResult[] {
  const fittings: FittingResult[] = [];
  const FITTINGS_DATA = (getFittings() || {}) as Record<string, Record<string, unknown>>;

  switch (connectionType) {
    case "sprinkler": {
      // Pipe -> Sprinkler: swing joint assembly
      const swingJoint = pipeDiameter <= 25 ? "Hunter SJ-506" : "Hunter SJ-512";
      fittings.push({ fitting: swingJoint, catalog: FITTINGS_DATA[swingJoint] as Record<string, unknown> | undefined, reason: "swing_joint" });
      const saddle = `SaddleClamp-${pipeDiameter <= 25 ? 25 : 32}`;
      if (FITTINGS_DATA[saddle]) {
        fittings.push({ fitting: saddle, catalog: FITTINGS_DATA[saddle] as Record<string, unknown> | undefined, reason: "saddle_clamp" });
      }
      break;
    }
    case "valve": {
      // Pipe -> Valve: adapter fitting
      const vSizeMm = valveSize || 25;
      let adapterKey: string;
      if (vSizeMm <= 25) {
        adapterKey = "MaleAdapter-25-3/4";
      } else if (vSizeMm <= 32) {
        adapterKey = "MaleAdapter-32-1";
      } else {
        adapterKey = "MaleAdapter-32-1";
      }
      if (FITTINGS_DATA[adapterKey]) {
        fittings.push({ fitting: adapterKey, catalog: FITTINGS_DATA[adapterKey] as Record<string, unknown> | undefined, reason: "valve_adapter" });
      }
      break;
    }
    case "pipe": {
      // Mainline -> Lateral: tee fitting
      const teeSize = pipeDiameter <= 25 ? 25 : 32;
      const teeKey = `Tee-${teeSize}`;
      if (FITTINGS_DATA[teeKey]) {
        fittings.push({ fitting: teeKey, catalog: FITTINGS_DATA[teeKey] as Record<string, unknown> | undefined, reason: "tee" });
      }
      break;
    }
    default:
      break;
  }

  // Reducer for diameter transitions
  if (targetDiameter && targetDiameter !== pipeDiameter) {
    const from = Math.max(pipeDiameter, targetDiameter);
    const to = Math.min(pipeDiameter, targetDiameter);
    const reducerKey = `Reducer-${from}-${to}`;
    if (FITTINGS_DATA[reducerKey]) {
      fittings.push({ fitting: reducerKey, catalog: FITTINGS_DATA[reducerKey] as Record<string, unknown> | undefined, reason: "reducer" });
    }
  }

  // Elbow for direction changes
  if (angle !== null && angle !== undefined && angle > 30) {
    const elbowSize = pipeDiameter <= 25 ? 25 : 32;
    const elbowKey = `Elbow-${elbowSize}`;
    if (FITTINGS_DATA[elbowKey]) {
      fittings.push({ fitting: elbowKey, catalog: FITTINGS_DATA[elbowKey] as Record<string, unknown> | undefined, reason: "elbow" });
    }
  }

  return fittings;
}

/** Analyze all pipe connections in a project and return auto-added components. */
export function generateAutoComponents({
  pipes,
  valveObjects,
}: {
  pipes: Pipe[];
  valveObjects: ValveObject[];
}): AutoComponent[] {
  const safePipes = pipes || [];
  const safeValves = valveObjects || [];
  const components: AutoComponent[] = [];
  const valveCatalog = (getValves() || {}) as Record<string, { sizeMm?: number }>;
  const pipeMap = new Map(safePipes.map(p => [p.id, p]));

  function processSnap(snap: { type: string; id?: number } | null | undefined, pipeRef: Pipe, end: string) {
    if (!snap) return;
    const d = pipeRef.diameter || 25;
    if (snap.type === "sprinkler") {
      const fits = getRequiredFittings({ connectionType: "sprinkler", pipeDiameter: d });
      fits.forEach(f => components.push({ ...f, pipeId: pipeRef.id, end }));
    } else if (snap.type === "valveObj") {
      const vo = safeValves.find(v => v.id === snap.id);
      const valveData = vo ? valveCatalog[vo.model] : null;
      const vSizeMm = valveData ? valveData.sizeMm : 25;
      const fits = getRequiredFittings({ connectionType: "valve", pipeDiameter: d, valveSize: vSizeMm });
      fits.forEach(f => components.push({ ...f, pipeId: pipeRef.id, end }));
    } else if (snap.type === "pipe") {
      const targetPipe = pipeMap.get(snap.id ?? -1);
      const targetD = targetPipe ? (targetPipe.diameter || 25) : d;
      const fits = getRequiredFittings({ connectionType: "pipe", pipeDiameter: d, targetDiameter: targetD });
      fits.forEach(f => components.push({ ...f, pipeId: pipeRef.id, end }));
    }
  }

  for (const pipe of safePipes) {
    processSnap(pipe.startSnap, pipe, "start");
    processSnap(pipe.endSnap, pipe, "end");

    // Direction changes at intermediate pipe points
    const d = pipe.diameter || 25;
    if (pipe.points && pipe.points.length > 2) {
      for (let i = 1; i < pipe.points.length - 1; i++) {
        const ang = segmentAngle(pipe.points[i - 1], pipe.points[i], pipe.points[i + 1]);
        if (ang > 30) {
          const fits = getRequiredFittings({ connectionType: null, pipeDiameter: d, angle: ang });
          fits.forEach(f => components.push({ ...f, pipeId: pipe.id, pointIndex: i }));
        }
      }
    }
  }

  return components;
}
